/**
 * AppCard - Molecule component for displaying application summary
 */
import React from 'react';
import { AppData } from '../../services/apiService';
import AppStatus, { AppStatusType } from '../atoms/AppStatus';

interface AppCardProps {
  app: AppData;
  onClick?: (app: AppData) => void;
  compact?: boolean;
  showStats?: boolean;
  className?: string;
  viewMode?: 'grid' | 'list';
}

const AppCard: React.FC<AppCardProps> = ({
  app,
  onClick,
  compact = false,
  showStats = true,
  className = '',
  viewMode = 'grid'
}) => {
  const getAppStatus = (app: AppData): AppStatusType => {
    if (!app.is_active) return 'inactive';
    if (!app.is_enabled) return 'error';
    return 'active';
  };

  const getAppTypeIcon = (appType?: string) => {
    switch (appType) {
      case 'spa':
        return '🌐';
      case 'native':
        return '📱';
      case 'regular_web':
        return '💻';
      case 'machine_to_machine':
        return '🤖';
      default:
        return '📱';
    }
  };

  const formatTimeAgo = (dateString?: string) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const getTrendIcon = (feedbackCount: number) => {
    if (feedbackCount === 0) return '📊';
    if (feedbackCount < 10) return '📈';
    if (feedbackCount < 100) return '🔥';
    return '💥';
  };

  if (viewMode === 'list') {
    return (
      <div
        className={`
          card cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-primary-300
          ${className}
        `}
        onClick={() => onClick?.(app)}
        role="button"
        tabIndex={0}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.(app);
          }
        }}
        aria-label={`View details for ${app.name || app.display_name}`}
      >
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1 min-w-0">
            {/* Icon and Status */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              <span className="text-lg" title={app.app_type_display || 'Unknown Type'}>
                {getAppTypeIcon(app.app_type)}
              </span>
              <AppStatus 
                status={getAppStatus(app)}
                size="sm"
                showLabel={false}
              />
            </div>
            
            {/* App Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900 dark:text-stone-100 text-base truncate">
                {app.name || app.display_name || 'Unknown App'}
              </h3>
              <p className="text-sm text-slate-600 dark:text-stone-400 truncate">
                {app.description || `${app.app_type_display || 'Unknown Type'} • ${app.client_id || app.app_id}`}
              </p>
            </div>
            
            {/* Stats */}
            {showStats && (
              <div className="flex items-center space-x-6 flex-shrink-0">
                <div className="flex items-center space-x-1 text-sm text-slate-500 dark:text-stone-400">
                  <span>{getTrendIcon(app.feedback_count)}</span>
                  <span className="font-medium text-slate-900 dark:text-stone-100">
                    {app.feedback_count}
                  </span>
                  <span>feedback</span>
                </div>
                
                <div className="flex items-center space-x-1 text-sm text-slate-500 dark:text-stone-400">
                  <span>🕐</span>
                  <span>{formatTimeAgo(app.last_feedback_at)}</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Arrow indicator */}
          <div className="flex-shrink-0 text-slate-400 dark:text-stone-500 ml-4">
            →
          </div>
        </div>
      </div>
    );
  }

  // Grid view (default)
  return (
    <div
      className={`
        card cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-primary-300
        ${className}
      `}
      onClick={() => onClick?.(app)}
      role="button"
      tabIndex={0}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(app);
        }
      }}
      aria-label={`View details for ${app.name || app.display_name}`}
    >
      <div className={compact ? 'p-3' : 'p-4'}>
      {/* Header with status and type */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <span className="text-lg" title={app.app_type_display || 'Unknown Type'}>
            {getAppTypeIcon(app.app_type)}
          </span>
          {!compact && (
            <AppStatus 
              status={getAppStatus(app)}
              size="sm"
              showLabel={false}
            />
          )}
        </div>
        
        {compact && (
          <AppStatus 
            status={getAppStatus(app)}
            size="sm"
            showLabel={false}
          />
        )}
      </div>

      {/* App name and description */}
      <div className="mb-3">
        <h3 className={`font-semibold text-slate-900 dark:text-stone-100 mb-1 ${compact ? 'text-sm' : 'text-base'}`}>
          {app.name || app.display_name || 'Unknown App'}
        </h3>
        
        {!compact && app.description && (
          <p className="text-sm text-slate-600 dark:text-stone-400 line-clamp-2">
            {app.description}
          </p>
        )}
        
        {compact && (
          <p className="text-xs text-slate-500 dark:text-stone-400 truncate">
            {app.client_id || app.app_id}
          </p>
        )}
      </div>

      {/* Stats section */}
      {showStats && (
        <div className="flex items-center justify-between text-sm text-slate-500 dark:text-stone-400">
          <div className="flex items-center space-x-1">
            <span>{getTrendIcon(app.feedback_count)}</span>
            <span className="font-medium text-slate-900 dark:text-stone-100">
              {app.feedback_count}
            </span>
            <span>feedback</span>
          </div>
          
          <div className="flex items-center space-x-1">
            <span>🕐</span>
            <span>{formatTimeAgo(app.last_feedback_at)}</span>
          </div>
        </div>
      )}

      {/* Client ID for debugging/admin */}
      {!compact && (
        <div className="mt-3 pt-3 border-t border-grey-200 dark:border-slate-700">
          <div className="flex items-center justify-between text-xs text-slate-500 dark:text-stone-400">
            <span>Client ID: {app.client_id || app.app_id || 'N/A'}</span>
            <span>{app.app_type_display || 'Unknown Type'}</span>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AppCard;